import React, { useState, useRef } from 'react';
import './Articles.css';

interface Article {
  id: string;
  title: string;
  author: string;
  publication?: string;
  date?: string;
  content: string;
  type: 'profile' | 'news' | 'feature';
}

const Articles: React.FC = () => {
  const [selectedArticle, setSelectedArticle] = useState<string | null>(null);
  const [isReading, setIsReading] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [currentSpeech, setCurrentSpeech] = useState<SpeechSynthesisUtterance | null>(null);
  const articlesRef = useRef<HTMLElement>(null);

  const articles: Article[] = [
    {
      id: 'artist-profile',
      title: 'Artist Profile: Hector Diaz',
      author: 'Alisha Zilk',
      publication: 'Tacoma Arts Commission',
      content: `In a loft crammed with years of work and an unending array of assorted odds and ends, I spent four hours listening to the story of the remarkable life of Hector Diaz, or "just Diaz", as he prefers to be called. The better half of our conversation consisted of his history-making trip to the Soviet Union. Last September, he and nine other American artists spent three and a half weeks traveling to the Russian cities of Riga, Leningrad and Moscow. The ten artists were chosen from a nation-wide competition, and they put on exhibits of their work in cooperation with a group of Soviet artists, promoting the artistic and cultural freedom now allowed in the Soviet Union.

Diaz noted that the one thing which left a lasting impression was the incredible emotional response demonstrated by the Russian people. "Soviet art is full of strong emotions," observed Diaz, "the struggle of life, poverty, anger and freedom." But the calm serenity conveyed in his works caused some to cry "tears of joy."

An example Diaz gave was of a young girl in Riga who spent over an hour just staring at one of his pieces, entitled "Kaleidoscope." Curious as to why she remained there for so long, Diaz approached her and asked if something about the painting bothered her. She replied that simply gazing at the colors and the contents of the picture gave her an inner feeling of indescribable peace. It allowed her to escape her depressing and troubled life in Russia and create a fantasy world of her own within the painting.

Ultimately, the trip was a huge success. All three shows were overflowing with eager citizens, hungry for the chance to view contemporary Western artwork. Enthusiasm was so great that Diaz said there was a possibility of a similar showing here in the United States this fall.

Diaz' paintings reflect a childhood spent in Brooklyn, New York. His favorite theme is street corners at night, which is where he used to pass the time hanging out with his friends, singing the old songs, and talking about the future. Continually experimenting with his medium, Diaz prefers to use household spraycan paint, citing the variety he is able to achieve. Another experiment is with rollers—from wall-painting rollers to small, handmade ones. Diaz has found he can build up layers of paint, creating not only a depth of color but a unique texture as well. Both these techniques perplexed the Russian audience. In Russia, many viewers were unfamiliar with these varied techniques and his works done with rollers were often mistaken for brushstrokes. "When they found out what I actually used, they were really amazed," chuckles Diaz.

This coming September, Diaz will have a show at the newly remodeled Tacoma Public Library's Handforth Gallery.`,
      type: 'profile'
    },
    {
      id: 'diaz-art-youth-program',
      title: 'Diaz Art for Youth Program Expands',
      author: 'Tacoma Arts Commission',
      content: `The Diaz Art for Youth Program has announced further expansion of its services as well as a new location for classes.

The history of this program has been impressive since the spring of 1993, 85% of the students involved have completed the program, and founder/mentor Hector Diaz reports a "99% positive adjustment in individual attitude and behavior."

Art for Youth is a free after-school visual art program designed for students ages 13-19. For sixteen weeks, the program offers classes in visual arts, using a unique teaching technique that integrates various mediums, formats and styles in a manner that makes it necessary for the student to make progressive choices. Over ten different art forms are involved.

While not claiming to make artists out of these students, the program does encourage youngsters to view and deal with difficult situations in a more creative manner. Carry-over of this skill in their "real lives" is a valuable asset that benefits both the students and our community.

The young people are introduced to the arts community by visiting venues and galleries throughout downtown Tacoma, especially the Tacoma Art Museum. Most of them have never been in such surroundings. Diaz believes that this introduction works well, that "they seem to understand right away, and they open up and know that it's all right to be creative, and respected for your efforts."

Along with learning correct ways to use materials, tools and equipment, students are encouraged to experience the positive as well as negative aspects of creating. Interpretations of their surroundings, possibilities and reflective memory are transformed into art that is appreciated and valued.

Particularly influential is the one-on-one guidance offered, which fosters both self-esteem and the retention of each learned skill.

The program presently accommodates 36 students per year, recruited from local high schools. The 1995 goal of Diaz Art for Youth is to double this number by the end of the year. Currently, the Board of Directors are actively seeking funds.

Diaz Art for Youth welcomes visitors at the new gallery/workshop at 747 St. Helens. Classes are held Tuesday and Thursday, from 3 p.m.-6 p.m.

The Man Behind the Program

Remembering his own difficulties as a teenager led Hector Diaz to develop his Art for Youth Program.

Diaz was born on the Island of Puerto Rico over fifty years ago. He expressed his interest in art by creating charcoal drawings on dried banana leaves.

When Diaz was seven years old, his family moved to Brooklyn. Two factors developed his talents: visits to all the city's museums and major art galleries, and his (temporarily) poor English skills. The teachers at his new school encouraged him to use crayons and paper to express himself. These crayons of many colors became tools for a new voice, and the beginning of Hector Diaz, the artist.

Growing up in New York proved to be a challenging and difficult experience. Education came much later. Diaz earned his GED at 26. Moving to Washington State, he crammed five years of education into three, graduating from the Burnley Professional School for Art in 1974. Despite the reality of being a "starving artist", over the years Diaz has received awards for his work and is now a well-known and respected artist.`,
      type: 'news'
    },
    {
      id: 'night-vision',
      title: 'Night Vision: While Others See Darkness, Tacoma Artist Sees the Spirit Inside Us All',
      author: 'Laura J. Aymond',
      publication: 'The News Tribune',
      content: `In 1990, longtime Tacoma resident Hector Diaz made history. Diaz was one of 10 American artists invited to exhibit in the cities of Moscow and Leningrad and in Riga, Latvia, providing a first opportunity for the former Soviet Union and the United States to communicate via art.

That experience made a deep impression on Diaz, one that continues to influence his work and his life today. Soon thereafter, he discovered that a Latvian woman had been so moved by one of his paintings that she wept while viewing it. "They told me the truth about my paintings," Diaz recalls.

Diaz had always believed that he could communicate the internal human experience through art. That belief was confirmed by this Latvian woman's tears as she perceived the emotional message in his work.

"She's a changed person," he said. "I found light in that," he said of her reaction. His portraits are semi-cubist statements, showing different faces in one face and his Russian impressions, paintings based on his trip to the Soviet Union.

Diaz, who refers to himself as a nocturnal painter, is nationally recognized for the street-corner scenes of his old New York neighborhoods at night. The Puerto Rican-born, Brooklyn-raised artist's fascination with light in the dark plays itself out on the stage of his canvas, evoking memories of his own youth.

"I want moonlight, not sunlight," he said. "It's more personal, more romantic."

Street lights, windows, doors, moonlight and neon reflecting off faces, streets and buildings are primary elements in his work.

"There's a story behind every window — the people who live there," he said. "Back in New York you didn't need to read the newspapers — you just looked out the window."

Ed Castro, a friend who grew up in the same neighborhood as Diaz but never knew each other until they met years later in Tacoma, speculated on some of the reasons they might paint nocturnal scenes: "We functioned better at night — it was our playground," he said. "On the streets as kids, we found that some place where we'd all be cool. There was a sense of togetherness or adventure at night."

"We weren't afraid of the darkness. It was like a friend."

Diaz said he paints "from my memory. From my heart. I believe in a guiding spirit." What he sees and what he feels guides his artistic vision.

Seeking art "deep from the heart," Diaz began his artistic journey in Puerto Rico. "I wish I could afford proper art supplies," he said. "I loved the smell of crayons, their colors. I couldn't speak English," when his family moved to Brooklyn.

As a boy, he taught himself by studying and copying drawings. Understanding the relationship with poverty, his family required him to leave school at 16 to support them through various jobs: shoe-shine boy, factory worker, fruit market sales. But he continued to draw and paint during his free time.

His studio is his sanctuary. "People see that I live in a different world," he said. "When I paint there has to be silence. The room is filled with it. My thoughts are clear and my heart speaks."

The path to becoming an artist wasn't easy. At age 26, he earned his GED and was accepted into the Seattle Art Institute. He found that art gave his life meaning and purpose.

"My art is like building a bridge," he explains. "If you do it right, people can cross over safely. I want to give something back to the people. My view is that they will appreciate you if you give them something real."

The images he paints capture the risk and romance that has been embedded in the night. Diaz has been blessed with the ability to see beauty in darkness, turning his personal experiences into universal expressions of the human spirit.

"That is the power as an artist," he said. "I want people to feel something they've never felt before when they see my work. I want them to think about something they've never had before."`,
      type: 'feature'
    },
    {
      id: 'tacoma-plays-art-show',
      title: 'Tacoma Plays A.V. Show: Young Artists Showcase Their Talents',
      author: 'Maria Sanchez Bradley',
      content: `Art, Music, Food, paintings, watercolors, more music, more food, sculptures, beaten metal masks, more art, and lots of people viewing lots of artworks created by over 20 young artists made "Tacoma Plays A.V. Show" a remarkable success. The show moved from the Sales Street gallery, where it shared space with artists from Oakwood Elementary Lettered Program and Centro Latino, to the U.S. Bank across from the Sheraton where it remained through First Night.

In the Westside neighborhood, a renowned award-winning local artist and father of three saw the lack of positive activities to occupy young people after school and realized that "there was a lot of talent out there and not enough people reaching out to them." This inspired him to start Diaz Art for Youth last year. Three sessions have now graduated over 40 students.

The success of the 16-week intensive art course offered hands-on, one-on-one professional instruction in over twelve different art forms from Diaz himself. The program has won accolades and recognition from the Tacoma Arts Commission, the South End Community Artist Trust, Safe Streets, and the Tacoma Art Museum, and was a precursor for the Hilltop Artists in Residence Program.

"The young people that come here don't have to be artists," said Diaz. "They can be psychologists, or architects, or something else. They come here to find themselves with higher expectations."

A dropout at 16 and former gang leader in the most violent streets of Brooklyn, born in Puerto Rico, he relates well to the young people he reaches. He honors his mother, who raised seven children (all college graduates) as a single mom, by using her teaching methods of love and discipline as the foundation of his Art Program.

The program features works by Adrian Brownstein, Lisa Briegs, Sean Becker, Tammy Chrisalys Ivriquez, Tom Jeffers, Tim Smith, Kevin Ivriquez, Jack Cruz, Jill Camilia, Brett, William Francis Haddock, Carlos Martinez, Austin Henry, Gilda Mendoza, Yamiley Armando Ortiz, Filiberto Katie Ralya, Kurt Rodriguez, Sia Ruiz, Laura Ruiz, Ruth Ruiz, and Jennifer Webster.

Students represented Lincoln High School, Stadium, Spanaway and Erie High Schools, Bethel Middle School, Sprinker and Fircrest Elementary Schools. The program runs year round in Tacoma, Washington on Tuesdays and Thursdays from 3-6 PM. To enroll, call 572-3175.`,
      type: 'news'
    },
    {
      id: 'art-galleries-being-erased',
      title: 'Art Galleries Are Being Erased: Dreams of Lively Arts Scene End for 2 of Tacoma\'s Art Entrepreneurs',
      author: 'Douglas McLennan',
      publication: 'The News Tribune',
      content: `In Hector Diaz's vision, the streets of downtown Tacoma are lined with galleries, coffee shops line the streets, movie theaters and stores feed one another, and the theaters of the Broadway Center aren't the only arts venue open every night.

Farfetched?

Perhaps, but many local business people and politicians share Diaz's vision and have staked the revitalization of downtown on a surge in the arts. They're convinced that if you build it, people will come downtown. Open coffee bars and galleries around the theaters, and maybe they'll stay awhile. Open stores and restaurants around the galleries, and the economic rebirth will be complete.

"Why not?" asks Diaz, an artist who moved to Tacoma in the early 1970s after growing up in Brooklyn. "In Greenwich Village 30 years ago, first the artists came and made it an interesting place to be, then educated people moved to that area, and then it became a social place. It became the most sought after place to live and work in the city. It could happen here."

"I keep telling people."

But he won't be saying it much longer. Unable to make it work in an empty Broadway storefront, Diaz talks now about how he's disappointed in Tacoma, about how he had tried to make it as an artist here, about his inability to see the unfulfilled promise of the arts community supporting the community. When his gallery closes later this summer, he says, "I'm leaving the paint and canvas behind and going to New York."

Tired of trying to make things happen in Tacoma, Diaz will join another casualty: the Merino Arts Center run by local artist Denise Smith. Like Diaz, Smith, an artist herself, was inspired by what seemed like a promising future in Tacoma.

"It was great community support," Smith says, "But there just hasn't been much interest from buyers in the Broadway Business District. We expected it so much more."

Today both the Merino Arts Center and Continuous Spectrum Gallery are closing. Several galleries launched during the last year, buoyed by the idea of a gallery district, have struggled or closed their doors. Still, some arts lovers say things are improving.

"Two years ago a Third Thursday Art Walk started to get folks into galleries," Smith says. "Young artists have moved here, attracted by cheap studio space, a supportive arts community, and downtown's potential for becoming a vibrant place for both artists and arts lovers. It's a natural progression," she says.

"People don't support our local artists. They go to Seattle if they want to buy something. We need more people here," says Hector Diaz.

Despite the setbacks, Diaz's vision of a thriving arts district continues to inspire others who believe in downtown Tacoma's potential for cultural renaissance.`,
      type: 'feature'
    },
    {
      id: 'first-night-gallery',
      title: 'First Night Gallery Showcases Local Artists',
      author: 'Robert E. Sharp',
      publication: 'The News Tribune',
      date: 'December 29, 1992',
      content: `On New Year's Eve under the direction of Lynn Johnson, local artist and curator, more than 30 artists from throughout the greater Puget Sound region will have their work on display at the First Night Gallery, in Room 104 at the University of Puget Sound Law School, 1111 N. Broadway.

In addition to the pieces in the gallery, a number of artists' works will be featured in six display windows of various Broadway storefronts throughout the day beginning at 2 p.m. First Night audiences will have the opportunity, many for the first time, to view in one location a selection of artwork from the expanding core of Pierce County area visual artists.

Additional artists from throughout the Puget Sound region will also be included, providing a well-rounded sampling of Washington artists.

"I'm looking forward to giving the many competent artists in the area a chance to be recognized by the general public for the work they do," said Johnson from his studio in the Merino Arts Center. "It's a great opportunity for everyone involved."

Putting together showings for artists and bringing something new to the public is nothing new to Johnson. Besides being a full-time artist, Johnson currently curates both the Tacoma Little Theater gallery and the Pacific Rim Alternative Arts Gallery, two venues which each provide an uncommon audience.

"It's unfortunate, but there aren't many quality locations for artists to display in the Tacoma area," said Johnson. "The First Night Gallery provides artists the opportunity to show to the public for one night out of the year as part of a locally-sponsored annual event."

For the past two decades Johnson has been working hard to help ensure that the arts receive the increased visibility they deserve. This is precisely the type of awareness that the First Night Gallery aims to promote by providing the general public with direct contact with a variety of different artists from the region.

To help ensure that the gallery's first showing is a success, Johnson has called upon the talents of some of the more recognized artists in the region.

Tacoma painter Hector Diaz has been an integral part of the local arts scene for more than twenty years. "Things are a lot different today," said Diaz. "When I first moved to Tacoma there wasn't nearly as much unity between the artists."

In the '70s Diaz had his own gallery for a while but couldn't find the same level of support he says exists today. "In the past few years there has really been a sense of community developing around Tacoma artists and people in Seattle are beginning to take notice," Diaz is optimistic. "I think the timing is perfect."

Sylvia Nichols is a sculptor living in Olympia who recently concluded a showing in the Tacoma area. Nichols feels that the city has a lot of potential waiting to be explored. "From experience I don't see any reason why Tacoma can't compete with Seattle in the arts," she said.

"There seems to be a lot of interest among artists and I think the First Night gallery will help promote that."

Nichols points out her current works in clay deal with traditional vessel forms, but she's turned them into non-traditional ideas and testing their limitations. The First Night Gallery affords a new way to connect with a new audience.`,
      type: 'news'
    },
    {
      id: 'third-thursday-art-walk',
      title: 'Tacoma Third Thursday Art Walk',
      author: 'Tacoma Arts Commission',
      content: `What is going on with the visual arts in Tacoma? Despite the strengths of private and college galleries, an excellent museum, the growth of cafe salons and artists studios, many believed a visual art scene just didn't exist in Tacoma.

They are about to be proved wrong. Tacoma Third Thursday, September 16 — a monthly art walk involving over 20 businesses in Tacoma's downtown, Union Station and Warehouse Districts. Launched by a group of concerned but optimistic administrators, Third Thursday grows from an otherwise dormant visual arts scene laid out in the city's core.

From 5:00 to 8:00 pm all are welcome to stroll freely and enjoy art, crafts, antiques and food. Participants include: Tacoma Art Museum, American Art Gallery, Kathy Kaperick Gallery, Freighthouse Art Gallery, Kittredge Gallery, KeyBank, Skookum Row merchants and other galleries and art related businesses. Maps showing all locations can be picked up at any site or by calling 272-7131.

Third Thursday promises to stimulate and synergize visual artists and arts lovers. It will create nightlife in the downtown center and will provide incentives for increasing numbers of artists to participate, giving birth to new opportunities for local artists and the community.

The September walk also features the Shoes Competition, a show of artist-made shoes benefiting AIDS Walk. The new Commencement Art Gallery will be among the featured venues.

This monthly event represents a significant step toward building the vibrant arts community that artists like Hector Diaz have long envisioned for downtown Tacoma. By creating regular opportunities for public engagement with local art, Third Thursday aims to foster the cultural renaissance that many believe is essential for the city's revitalization.`,
      type: 'news'
    },
    {
      id: 'seniors-making-art',
      title: 'Seniors Making Art: Creativity Knows No Age',
      author: 'Community Arts Program',
      content: `"I've always been interested in the knowledge and imagination and creativity of older people. I've always felt that they offer the most to society. And that for some reason, their experiences in the latter part of their lives and their situations don't set them up very well to be able to express this. Seniors Making Art is an opportunity for us to learn from seniors - to try to encourage them in the way to be creative. To show them to use their hands to make things. From this they are going to get a great deal of pleasure. I was sure of this as a concept, and it's proven to be the case." - Dale Chihuly, Founder of Seniors Making Art

The Seniors Making Art program has found a dedicated instructor in Hector Diaz, who brings his expertise in multiple art forms to senior centers throughout Tacoma. The classes focus on various techniques including linoleum block prints (lino cuts), watercolor painting, and self-portraits, allowing seniors to explore different mediums and discover their artistic voices.

At the Lighthouse Senior Center in Tacoma, Hector conducts classes with typically 12 to 15 participants, with a maximum of 20. The intimate setting allows for personalized instruction, with Hector demonstrating proper brush selection and technique to students like Laura Black, who observes attentively as he explains the nuances of different brushes for various artistic effects.

"Selection of the brush can determine the quality of your work," Hector explains to his students. His patient teaching style is evident as he works with participants like Lucille Williams, who watches intently as he demonstrates proper brushing techniques to the entire class.

The program has attracted participants with varying levels of experience. Zella Sanchez, who carefully traces a leopard design onto Plexiglas during one session, represents the program's success in reaching seniors who never considered themselves artists. "I've never painted a thing in my life," claims Sanchez, who now has a painting displayed at the Seattle Art Museum.

The art classes at the Beacon Senior Center began February 19, meeting for two hours per session and running for 10 weeks. The program demonstrates Hector's commitment to bringing art education to all members of the community, regardless of age, proving that creativity and artistic expression have no expiration date.

Through Seniors Making Art, Hector continues his mission of using art as a tool for personal growth, community building, and self-discovery, extending his impact beyond youth programs to encompass the entire community.`,
      type: 'feature'
    }
  ];

  const startReading = (content: string) => {
    // Stop any current speech
    if (currentSpeech) {
      speechSynthesis.cancel();
    }

    const utterance = new SpeechSynthesisUtterance(content);
    utterance.rate = 0.8;
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onstart = () => {
      setIsReading(true);
      setIsPaused(false);
    };

    utterance.onend = () => {
      setIsReading(false);
      setIsPaused(false);
      setCurrentSpeech(null);
    };

    utterance.onerror = () => {
      setIsReading(false);
      setIsPaused(false);
      setCurrentSpeech(null);
    };

    setCurrentSpeech(utterance);
    speechSynthesis.speak(utterance);
  };

  const pauseReading = () => {
    if (speechSynthesis.speaking && !speechSynthesis.paused) {
      speechSynthesis.pause();
      setIsPaused(true);
    }
  };

  const resumeReading = () => {
    if (speechSynthesis.paused) {
      speechSynthesis.resume();
      setIsPaused(false);
    }
  };

  const stopReading = () => {
    speechSynthesis.cancel();
    setIsReading(false);
    setIsPaused(false);
    setCurrentSpeech(null);
  };

  const getArticleIcon = (type: string) => {
    switch (type) {
      case 'profile': return '👤';
      case 'news': return '📰';
      case 'feature': return '⭐';
      default: return '📄';
    }
  };

  const getArticleTypeLabel = (type: string) => {
    switch (type) {
      case 'profile': return 'Artist Profile';
      case 'news': return 'News Article';
      case 'feature': return 'Feature Story';
      default: return 'Article';
    }
  };

  const selectedArticleData = selectedArticle ? articles.find(a => a.id === selectedArticle) : null;

  return (
    <section className="articles-section" id="articles" ref={articlesRef}>
      <div className="articles-container">
        <div className="articles-header">
          <div className="header-badge">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM5 19V5H19V19H5Z" fill="currentColor"/>
              <path d="M7 7H17V9H7V7ZM7 11H17V13H7V11ZM7 15H14V17H7V15Z" fill="currentColor"/>
            </svg>
            <span>Articles & Features</span>
          </div>
          <h2 className="articles-title">Read About Hector</h2>
          <p className="articles-subtitle">
            Discover the stories, interviews, and features that capture the essence of Hector Díaz's artistic journey
          </p>
          <div className="title-decoration">
            <div className="decoration-line"></div>
            <div className="decoration-star">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L13.5 7.5L19 9L13.5 10.5L12 15L10.5 10.5L5 9L10.5 7.5L12 2Z" fill="currentColor"/>
              </svg>
            </div>
            <div className="decoration-line"></div>
          </div>
        </div>

        {!selectedArticle ? (
          <div className="articles-grid">
            {articles.map((article) => (
              <div key={article.id} className="article-card" onClick={() => setSelectedArticle(article.id)}>
                <div className="card-background-effect"></div>
                <div className="article-header">
                  <div className="article-type-badge">
                    <span className="type-icon">{getArticleIcon(article.type)}</span>
                    <span className="type-label">{getArticleTypeLabel(article.type)}</span>
                  </div>
                </div>
                <h3 className="article-title">{article.title}</h3>
                <div className="article-meta">
                  <span className="article-author">By {article.author}</span>
                  {article.publication && (
                    <span className="article-publication">{article.publication}</span>
                  )}
                </div>
                <p className="article-preview">
                  {article.content.substring(0, 150)}...
                </p>
                <div className="article-actions">
                  <button className="read-button">
                    <span className="button-icon">📖</span>
                    Read Article
                  </button>
                  <button
                    className="listen-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      startReading(article.content);
                    }}
                  >
                    <span className="button-icon">🔊</span>
                    Listen
                  </button>
                </div>
                <div className="card-accent-line"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="article-reader">
            <div className="reader-header">
              <button
                className="back-button"
                onClick={() => setSelectedArticle(null)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 11H7.83L13.42 5.41L12 4L4 12L12 20L13.41 18.59L7.83 13H20V11Z" fill="currentColor"/>
                </svg>
                Back to Articles
              </button>
              <div className="reader-controls">
                {!isReading ? (
                  <button
                    className="control-button listen"
                    onClick={() => selectedArticleData && startReading(selectedArticleData.content)}
                  >
                    <span className="button-icon">▶️</span>
                    Play Article
                  </button>
                ) : (
                  <div className="audio-controls">
                    {!isPaused ? (
                      <button
                        className="control-button pause"
                        onClick={pauseReading}
                      >
                        <span className="button-icon">⏸️</span>
                        Pause
                      </button>
                    ) : (
                      <button
                        className="control-button resume"
                        onClick={resumeReading}
                      >
                        <span className="button-icon">▶️</span>
                        Resume
                      </button>
                    )}
                    <button
                      className="control-button stop"
                      onClick={stopReading}
                    >
                      <span className="button-icon">⏹️</span>
                      Stop
                    </button>
                  </div>
                )}
              </div>
            </div>

            {selectedArticleData && (
              <article className="article-content">
                <div className="article-type-badge large">
                  <span className="type-icon">{getArticleIcon(selectedArticleData.type)}</span>
                  <span className="type-label">{getArticleTypeLabel(selectedArticleData.type)}</span>
                </div>
                <h1 className="article-title-full">{selectedArticleData.title}</h1>
                <div className="article-meta-full">
                  <span className="article-author">By {selectedArticleData.author}</span>
                  {selectedArticleData.publication && (
                    <span className="article-publication">{selectedArticleData.publication}</span>
                  )}
                  {selectedArticleData.date && (
                    <span className="article-date">{selectedArticleData.date}</span>
                  )}
                </div>
                <div className={`article-text ${isReading ? 'reading' : ''}`}>
                  {selectedArticleData.content.split('\n\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </article>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default Articles;